import { useState } from 'react';
import type { OperationLog } from '../types';

interface LogTableProps {
  logs: OperationLog[];
}

export default function LogTable({ logs }: LogTableProps) {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const toggleRow = (id: number) => {
    setExpandedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                日志ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                日志类型
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                模块
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                操作类型
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                被操作对象
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                操作员
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                IP地址
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                操作时间
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {logs.map((log) => (
              <>
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {log.id}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        log.logType === '操作日志'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {log.logType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {log.module}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {log.operationType || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {log.targetId ? (
                      <div>
                        <div className="font-medium">{log.targetName}</div>
                        <div className="text-xs text-gray-500">{log.targetId}</div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <div>
                      <div className="font-medium">{log.operatorName}</div>
                      <div className="text-xs text-gray-500">{log.operatorId}</div>
                      <div className="text-xs text-gray-500">{log.operatorRole}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <div>
                      <div>{log.ip}</div>
                      <div className="text-xs text-gray-500">{log.deviceInfo}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {log.operationTime}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    {(log.beforeContent || log.afterContent) && (
                      <button
                        onClick={() => toggleRow(log.id)}
                        className="text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
                      >
                        <i
                          className={`ri-${
                            expandedRows.includes(log.id) ? 'arrow-up' : 'arrow-down'
                          }-s-line mr-1`}
                        ></i>
                        {expandedRows.includes(log.id) ? '收起' : '查看'}详情
                      </button>
                    )}
                  </td>
                </tr>
                {expandedRows.includes(log.id) && (
                  <tr>
                    <td colSpan={9} className="px-4 py-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-4">
                        {log.beforeContent && (
                          <div>
                            <div className="text-xs font-semibold text-gray-700 mb-2">
                              变更前内容
                            </div>
                            <pre className="bg-white p-3 rounded border border-gray-200 text-xs text-gray-800 overflow-auto max-h-64">
                              {log.beforeContent}
                            </pre>
                          </div>
                        )}
                        {log.afterContent && (
                          <div>
                            <div className="text-xs font-semibold text-gray-700 mb-2">
                              变更后内容
                            </div>
                            <pre className="bg-white p-3 rounded border border-gray-200 text-xs text-gray-800 overflow-auto max-h-64">
                              {log.afterContent}
                            </pre>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
